import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  AlertTriangle,
  Clock,
  Zap,
  Settings,
  TrendingUp,
  BarChart3,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Trash2,
  RotateCcw,
  Calendar,
  Users,
  Gauge,
  Layers
} from 'lucide-react';
import api from '../../services/api';

const SMSPipelineManager = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [kafkaStats, setKafkaStats] = useState(null);
  const [pendingList, setPendingList] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [concurrency, setConcurrency] = useState(5);
  const [newConcurrency, setNewConcurrency] = useState(5);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, kafkaRes] = await Promise.all([
        api.get(`/admin/pipeline/stats?date=${date}`),
        api.get('/admin/pipeline/kafka-stats').catch(() => ({ data: { success: false } }))
      ]);
      if (statsRes.data.success) {
        setStats(statsRes.data);
        if (statsRes.data.kafka_stats) {
          setKafkaStats(statsRes.data.kafka_stats);
          setConcurrency(statsRes.data.kafka_stats.concurrency || 5);
          setNewConcurrency(statsRes.data.kafka_stats.concurrency || 5);
        }
      }
      if (kafkaRes.data.success && kafkaRes.data.stats) {
        setKafkaStats(kafkaRes.data.stats);
        setConcurrency(kafkaRes.data.stats.concurrency || 5);
        setNewConcurrency(kafkaRes.data.stats.concurrency || 5);
      }
    } catch (err) {
      console.error('Failed to fetch pipeline stats:', err);
    }
    setLoading(false);
  }, [date]);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api.get(`/admin/pipeline/pending?date=${date}&page=${pendingPage}&limit=50`);
      if (res.data.success) {
        setPendingList(res.data.data || []);
        setPendingTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch pending SMS:', err);
    }
  }, [date, pendingPage]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'pending') fetchPending(); }, [fetchPending, activeTab]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchStats();
      if (activeTab === 'pending') fetchPending();
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStats, fetchPending, activeTab]);

  const handleSetConcurrency = async () => {
    try {
      const res = await api.post('/admin/pipeline/kafka-concurrency', { concurrency: newConcurrency });
      if (res.data.success) {
        setConcurrency(newConcurrency);
        setActionResult({ type: 'success', message: res.data.message });
      }
    } catch (err) {
      setActionResult({ type: 'error', message: err.response?.data?.message || 'Failed to set concurrency' });
    }
    setTimeout(() => setActionResult(null), 5000);
  };

  const handleResolvePending = async (action) => {
    setConfirmAction(null);
    try {
      const res = await api.post('/admin/pipeline/resolve-pending', {
        date, action, reason: action === 'fail' ? 'Bulk resolved by admin - DLT/routing failures' : ''
      });
      if (res.data.success) {
        setActionResult({ type: 'success', message: res.data.message });
        fetchStats();
        fetchPending();
      }
    } catch (err) {
      setActionResult({ type: 'error', message: err.response?.data?.message || 'Failed' });
    }
    setTimeout(() => setActionResult(null), 5000);
  };

  const handleInvalidateCache = async () => {
    try {
      const res = await api.post('/admin/pipeline/invalidate-failover-cache');
      setActionResult({ type: 'success', message: res.data.message });
    } catch (err) {
      setActionResult({ type: 'error', message: 'Failed to invalidate cache' });
    }
    setTimeout(() => setActionResult(null), 5000);
  };

  const totalSMS = stats?.status_counts?.reduce((sum, s) => sum + s.count, 0) || 0;
  const deliveredCount = stats?.status_counts?.find(s => s.status === 'DELIVERED')?.count || 0;
  const pendingCount = stats?.status_counts?.find(s => s.status === 'PENDING')?.count || 0;
  const failedCount = stats?.status_counts?.filter(s => ['FAILED', 'EXPIRED', 'REJECTED'].includes(s.status))
    .reduce((sum, s) => sum + s.count, 0) || 0;
  const deliveryRate = totalSMS > 0 ? ((deliveredCount / totalSMS) * 100).toFixed(1) : 0;

  return (
    <div className="p-4 md:p-6 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-600" />
            SMS Pipeline Manager
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monitor, control, and resolve SMS processing pipeline issues</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white shadow-sm" />
          <button onClick={() => { setAutoRefresh(!autoRefresh); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 shadow-sm ${autoRefresh ? 'bg-green-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}>
            {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {autoRefresh ? 'Auto 5s' : 'Auto'}
          </button>
          <button onClick={fetchStats} disabled={loading}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 shadow-sm hover:bg-blue-700 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Action Result Toast */}
      {actionResult && (
        <div className={`p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${actionResult.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {actionResult.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {actionResult.message}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPICard icon={<Layers className="w-5 h-5 text-blue-600" />} label="Total SMS" value={totalSMS.toLocaleString()} color="blue" />
        <KPICard icon={<CheckCircle className="w-5 h-5 text-green-600" />} label="Delivered" value={deliveredCount.toLocaleString()} sub={`${deliveryRate}%`} color="green" />
        <KPICard icon={<Clock className="w-5 h-5 text-yellow-600" />} label="Pending" value={pendingCount.toLocaleString()} color="yellow" />
        <KPICard icon={<XCircle className="w-5 h-5 text-red-600" />} label="Failed" value={failedCount.toLocaleString()} color="red" />
        <KPICard icon={<Gauge className="w-5 h-5 text-purple-600" />} label="Avg Delay" value={`${(stats?.avg_delays?.avg_ingest_to_submit_sec || 0).toFixed(0)}s`} sub="ingest→submit" color="purple" />
        <KPICard icon={<Zap className="w-5 h-5 text-indigo-600" />} label="Workers" value={kafkaStats?.concurrency || '-'} sub={`avg ${kafkaStats?.avg_ms || 0}ms`} color="indigo" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border">
        {[
          { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-4 h-4" /> },
          { id: 'kafka', label: 'Kafka Workers', icon: <Zap className="w-4 h-4" /> },
          { id: 'pending', label: `Pending (${pendingCount})`, icon: <AlertTriangle className="w-4 h-4" /> },
          { id: 'connectors', label: 'Connectors', icon: <TrendingUp className="w-4 h-4" /> },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'}`}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab stats={stats} />}
      {activeTab === 'kafka' && (
        <KafkaTab kafkaStats={kafkaStats} concurrency={concurrency} newConcurrency={newConcurrency}
          setNewConcurrency={setNewConcurrency} handleSetConcurrency={handleSetConcurrency}
          handleInvalidateCache={handleInvalidateCache} />
      )}
      {activeTab === 'pending' && (
        <PendingTab pendingList={pendingList} pendingTotal={pendingTotal} pendingPage={pendingPage}
          setPendingPage={setPendingPage} stats={stats} confirmAction={confirmAction}
          setConfirmAction={setConfirmAction} handleResolvePending={handleResolvePending} date={date} />
      )}
      {activeTab === 'connectors' && <ConnectorsTab stats={stats} />}
    </div>
  );
};

// ─── KPI Card ────────────────────────────────────────────────────────────────
const KPICard = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2 mb-1">{icon}<span className="text-xs font-medium text-gray-500 uppercase">{label}</span></div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

// ─── Overview Tab ────────────────────────────────────────────────────────────
const OverviewTab = ({ stats }) => {
  if (!stats) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  const maxHourly = Math.max(...(stats.hourly || []).map(h => h.ingested), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Hourly Throughput */}
      <div className="bg-white rounded-xl p-5 shadow-sm border">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-500" /> Hourly Throughput
        </h3>
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {(stats.hourly || []).map(h => (
            <div key={h.hour} className="flex items-center gap-2 text-xs">
              <span className="w-8 text-right font-mono text-gray-500">{String(h.hour).padStart(2, '0')}h</span>
              <div className="flex-1 flex gap-0.5 h-5">
                <div className="bg-blue-500 rounded-sm h-full" style={{ width: `${(h.ingested / maxHourly) * 100}%` }}
                  title={`Ingested: ${h.ingested}`} />
              </div>
              <span className="w-16 text-right font-mono">
                <span className="text-blue-600">{h.ingested}</span>
                {h.delivered > 0 && <span className="text-green-600 ml-1">✓{h.delivered}</span>}
              </span>
            </div>
          ))}
          {(!stats.hourly || stats.hourly.length === 0) && <div className="text-gray-400 text-center py-4">No data</div>}
        </div>
      </div>

      {/* Delay Distribution */}
      <div className="bg-white rounded-xl p-5 shadow-sm border">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-500" /> Ingest → Submit Delay Distribution
        </h3>
        <div className="space-y-2">
          {(stats.delay_buckets || []).map(d => {
            const maxDelay = Math.max(...(stats.delay_buckets || []).map(x => x.count), 1);
            const pct = (d.count / maxDelay) * 100;
            const isWarning = ['2-5min', '5-10min', '10min+'].includes(d.bucket);
            return (
              <div key={d.bucket} className="flex items-center gap-2 text-xs">
                <span className={`w-16 text-right font-mono ${isWarning ? 'text-red-600 font-bold' : 'text-gray-500'}`}>{d.bucket}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                  <div className={`h-full rounded-full ${isWarning ? 'bg-red-400' : 'bg-blue-400'}`}
                    style={{ width: `${pct}%` }} />
                </div>
                <span className={`w-12 text-right font-mono ${isWarning ? 'text-red-600 font-bold' : 'text-gray-600'}`}>{d.count}</span>
              </div>
            );
          })}
          {(!stats.delay_buckets || stats.delay_buckets.length === 0) && <div className="text-gray-400 text-center py-4">No data</div>}
        </div>

        {/* Average Delays */}
        {stats.avg_delays && (
          <div className="mt-4 pt-3 border-t grid grid-cols-2 gap-2 text-xs">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="text-gray-500">Avg Ingest→Submit</div>
              <div className="text-lg font-bold text-blue-700">{stats.avg_delays.avg_ingest_to_submit_sec?.toFixed(0)}s</div>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <div className="text-gray-500">Avg End-to-End</div>
              <div className="text-lg font-bold text-green-700">{stats.avg_delays.avg_end_to_end_sec?.toFixed(0)}s</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-2">
              <div className="text-gray-500">Max Ingest→Submit</div>
              <div className="text-lg font-bold text-yellow-700">{stats.avg_delays.max_ingest_to_submit_sec?.toFixed(0)}s</div>
            </div>
            <div className="bg-red-50 rounded-lg p-2">
              <div className="text-gray-500">Max End-to-End</div>
              <div className="text-lg font-bold text-red-700">{stats.avg_delays.max_end_to_end_sec?.toFixed(0)}s</div>
            </div>
          </div>
        )}
      </div>

      {/* Status Breakdown */}
      <div className="bg-white rounded-xl p-5 shadow-sm border lg:col-span-2">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-500" /> Status Breakdown
        </h3>
        <div className="flex flex-wrap gap-3">
          {(stats.status_counts || []).map(s => {
            const colors = {
              DELIVERED: 'bg-green-100 text-green-800 border-green-200',
              PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
              FAILED: 'bg-red-100 text-red-800 border-red-200',
              EXPIRED: 'bg-orange-100 text-orange-800 border-orange-200',
              SENT: 'bg-blue-100 text-blue-800 border-blue-200',
              REJECTED: 'bg-pink-100 text-pink-800 border-pink-200',
            };
            const colorClass = colors[s.status] || 'bg-gray-100 text-gray-800 border-gray-200';
            return (
              <div key={s.status} className={`px-4 py-2 rounded-lg border ${colorClass}`}>
                <div className="text-xs font-medium">{s.status}</div>
                <div className="text-xl font-bold">{s.count.toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Kafka Workers Tab ───────────────────────────────────────────────────────
const KafkaTab = ({ kafkaStats, concurrency, newConcurrency, setNewConcurrency, handleSetConcurrency, handleInvalidateCache }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {/* Concurrency Control */}
    <div className="bg-white rounded-xl p-5 shadow-sm border">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <Settings className="w-4 h-4 text-blue-500" /> Kafka Consumer Concurrency
      </h3>
      <div className="space-y-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-xs text-blue-600 font-medium">Current Workers</div>
          <div className="text-4xl font-bold text-blue-800">{concurrency}</div>
          <div className="text-xs text-blue-500 mt-1">parallel goroutines processing SMS from Kafka</div>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Set New Concurrency (1-50)</label>
          <div className="flex gap-2">
            <input type="range" min="1" max="50" value={newConcurrency}
              onChange={e => setNewConcurrency(parseInt(e.target.value))}
              className="flex-1" />
            <input type="number" min="1" max="50" value={newConcurrency}
              onChange={e => setNewConcurrency(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-16 px-2 py-1 border rounded text-center text-sm" />
          </div>
          <div className="flex gap-1 mt-2">
            {[1, 5, 10, 20, 30, 50].map(n => (
              <button key={n} onClick={() => setNewConcurrency(n)}
                className={`px-2 py-1 rounded text-xs font-medium ${newConcurrency === n ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <button onClick={handleSetConcurrency} disabled={newConcurrency === concurrency}
          className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          <Zap className="w-4 h-4" /> Apply Concurrency: {newConcurrency}
        </button>
        <p className="text-xs text-gray-400">
          <strong>Recommendation:</strong> Set to 10-20 for peak traffic (1,900+ msgs/min). Default 5 handles ~2,000 msgs/min.
          Higher values use more CPU/DB connections.
        </p>
      </div>
    </div>

    {/* Live Stats */}
    <div className="bg-white rounded-xl p-5 shadow-sm border">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <Activity className="w-4 h-4 text-green-500" /> Live Consumer Stats
      </h3>
      {kafkaStats ? (
        <div className="grid grid-cols-2 gap-3">
          <StatBox label="Processed" value={kafkaStats.processed?.toLocaleString()} color="green" />
          <StatBox label="Errors" value={kafkaStats.errors?.toLocaleString()} color="red" />
          <StatBox label="Priority" value={kafkaStats.priority?.toLocaleString()} color="purple" />
          <StatBox label="Bulk" value={kafkaStats.bulk?.toLocaleString()} color="blue" />
          <StatBox label="Skipped" value={kafkaStats.skipped?.toLocaleString()} color="gray" />
          <StatBox label="Avg Process" value={`${kafkaStats.avg_ms}ms`} color="yellow" />
          <StatBox label="Queue Depth" value={kafkaStats.queue_depth?.toLocaleString()} color="orange" />
          <StatBox label="Last Batch" value={`${kafkaStats.last_batch_ms}ms`} color="indigo" />
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Kafka stats not available (worker may be on different process)
        </div>
      )}

      {/* Cache Control */}
      <div className="mt-4 pt-4 border-t">
        <h4 className="text-xs font-semibold text-gray-600 mb-2">Failover Cache</h4>
        <button onClick={handleInvalidateCache}
          className="px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg text-xs font-medium hover:bg-orange-100 flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Invalidate Failover Cache
        </button>
        <p className="text-xs text-gray-400 mt-1">Clear cached failover lookups. Use after changing routing policies.</p>
      </div>
    </div>
  </div>
);

const StatBox = ({ label, value, color }) => {
  const colors = {
    green: 'bg-green-50 text-green-700', red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700', purple: 'bg-purple-50 text-purple-700',
    yellow: 'bg-yellow-50 text-yellow-700', gray: 'bg-gray-50 text-gray-700',
    orange: 'bg-orange-50 text-orange-700', indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color] || colors.gray}`}>
      <div className="text-xs font-medium opacity-70">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
};

// ─── Pending Tab ─────────────────────────────────────────────────────────────
const PendingTab = ({ pendingList, pendingTotal, pendingPage, setPendingPage, stats, confirmAction, setConfirmAction, handleResolvePending, date }) => (
  <div className="space-y-4">
    {/* Pending Breakdown */}
    <div className="bg-white rounded-xl p-5 shadow-sm border">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-yellow-500" /> Pending SMS Breakdown
      </h3>
      <div className="flex flex-wrap gap-3 mb-4">
        {(stats?.pending_breakdown || []).map(p => (
          <div key={p.reason} className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
            <div className="text-xs text-yellow-600 font-medium">{p.reason}</div>
            <div className="text-xl font-bold text-yellow-800">{p.count}</div>
          </div>
        ))}
        {(!stats?.pending_breakdown || stats.pending_breakdown.length === 0) && (
          <div className="text-green-600 text-sm font-medium flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> No pending SMS!
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {pendingTotal > 0 && (
        <div className="flex gap-2 pt-3 border-t">
          <button onClick={() => setConfirmAction('fail')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1">
            <Trash2 className="w-4 h-4" /> Mark All FAILED ({pendingTotal})
          </button>
          <button onClick={() => setConfirmAction('retry')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-1">
            <RotateCcw className="w-4 h-4" /> Retry All ({pendingTotal})
          </button>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmAction && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-medium">
            Are you sure you want to {confirmAction === 'fail' ? 'mark all pending SMS as FAILED' : 'retry all pending SMS'}
            for {date}? ({pendingTotal} SMS)
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => handleResolvePending(confirmAction)}
              className="px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700">
              Yes, {confirmAction === 'fail' ? 'Mark Failed' : 'Retry All'}
            </button>
            <button onClick={() => setConfirmAction(null)}
              className="px-3 py-1.5 bg-white border text-gray-600 rounded text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {/* Pending SMS List */}
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="px-5 py-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Pending SMS List ({pendingTotal})</h3>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => setPendingPage(Math.max(1, pendingPage - 1))} disabled={pendingPage <= 1}
            className="px-2 py-1 border rounded disabled:opacity-30">Prev</button>
          <span className="text-gray-500">Page {pendingPage} of {Math.ceil(pendingTotal / 50) || 1}</span>
          <button onClick={() => setPendingPage(pendingPage + 1)} disabled={pendingPage >= Math.ceil(pendingTotal / 50)}
            className="px-2 py-1 border rounded disabled:opacity-30">Next</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Message ID</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">User</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Mobile</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Operator</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Remarks</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Created</th>
              <th className="px-3 py-2 text-center font-medium text-gray-500">Attempted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(pendingList || []).map(sms => (
              <tr key={sms.message_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-blue-600">{sms.message_id}</td>
                <td className="px-3 py-2">{sms.user_id}</td>
                <td className="px-3 py-2 font-mono">{sms.mobile_number}</td>
                <td className="px-3 py-2">{sms.operator || '-'}</td>
                <td className="px-3 py-2 max-w-xs truncate text-gray-500" title={sms.remarks}>{sms.remarks || '-'}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{sms.created_at?.replace('T', ' ').substring(0, 19)}</td>
                <td className="px-3 py-2 text-center">
                  {sms.send_attempted ? <span className="text-yellow-600">Yes</span> : <span className="text-gray-400">No</span>}
                </td>
              </tr>
            ))}
            {(!pendingList || pendingList.length === 0) && (
              <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-400">No pending SMS</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── Connectors Tab ──────────────────────────────────────────────────────────
const ConnectorsTab = ({ stats }) => (
  <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
    <div className="px-5 py-3 border-b">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-indigo-500" /> Connector Performance
      </h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-500">Connector</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Total</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Submitted</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Delivered</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Failed</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Pending</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Delivery %</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(stats?.connectors || []).map(c => {
            const delPct = c.total > 0 ? ((c.delivered / c.total) * 100).toFixed(1) : 0;
            return (
              <tr key={c.operator} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{c.operator}</td>
                <td className="px-4 py-2 text-right font-mono">{c.total.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-blue-600">{c.submitted.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-green-600">{c.delivered.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-red-600">{c.failed.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-yellow-600">{c.pending.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${parseFloat(delPct) >= 90 ? 'bg-green-100 text-green-700' : parseFloat(delPct) >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                    {delPct}%
                  </span>
                </td>
              </tr>
            );
          })}
          {(!stats?.connectors || stats.connectors.length === 0) && (
            <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No connector data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default SMSPipelineManager;
